import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { appConfirm } from '../utils/appDialog';

const DEFAULT_CONFIRM_MESSAGE = 'Discard unsaved changes?';

/**
 * Portal modal — renders at document.body so position:fixed is viewport-relative
 * (avoids misalignment from page transforms / depth layers).
 *
 * `confirmClose` guards the accidental-dismiss paths (Escape, backdrop click)
 * only; explicit Cancel buttons inside the modal still close directly.
 */
export default function Modal({
  open,
  onClose,
  children,
  overlayClassName = '',
  contentClassName = '',
  ariaLabel,
  confirmClose = false,
}) {
  const requestClose = useCallback(() => {
    if (!confirmClose) {
      onClose?.();
      return;
    }
    const message = typeof confirmClose === 'string' ? confirmClose : DEFAULT_CONFIRM_MESSAGE;
    void appConfirm(message).then((ok) => {
      if (ok) onClose?.();
    });
  }, [confirmClose, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, requestClose]);

  if (!open) return null;

  const overlayClasses = ['modal-overlay', overlayClassName].filter(Boolean).join(' ');
  const contentClasses = ['modal-content', contentClassName].filter(Boolean).join(' ');

  return createPortal(
    <div className={overlayClasses} onClick={requestClose} role="presentation">
      <div
        className={contentClasses}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
